import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslations } from "next-intl";
import { useState } from "react";

/**
 * 模块卡片组件的属性接口
 */
interface ModuleCardProps {
  titleKey: string; // 翻译文件中模块标题的key值
  imageUrl: string; // 模块图片的URL地址
}

/**
 * 模块卡片组件 - 支持点击翻转的3D卡片效果
 * 
 * 功能特点：
 * - 3D翻转动画：点击卡片可以翻转查看详细描述
 * - 防拖拽误触：在拖拽操作时不会触发卡片翻转
 * - 主题适配：根据暗黑/明亮模式自动调整颜色
 * - Hover效果：鼠标悬停时显示边框高亮效果
 * 
 * @param titleKey - 模块标题在翻译文件中的key
 * @param imageUrl - 模块图片的URL
 */
export default function ModuleCard({ titleKey, imageUrl }: ModuleCardProps) {
  const theme = useTheme(); // 获取当前主题配置
  const t = useTranslations("Citadel"); // 获取Citadel页面的翻译函数
  
  // === 卡片状态管理 ===
  const [isFlipped, setIsFlipped] = useState(false); // 卡片是否已翻转
  const [isDragging, setIsDragging] = useState(false); // 是否正在进行拖拽操作
  const [startPos, setStartPos] = useState({ x: 0, y: 0 }); // 鼠标按下时的初始位置

  /**
   * 处理鼠标按下事件
   * 记录初始位置，重置拖拽状态
   */
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(false);
    setStartPos({ x: e.clientX, y: e.clientY });
  };

  /**
   * 处理鼠标移动事件
   * 检测是否为拖拽操作（移动距离超过5px阈值）
   */
  const handleMouseMove = (e: React.MouseEvent) => {
    if (startPos.x === 0 && startPos.y === 0) return;
    
    const deltaX = Math.abs(e.clientX - startPos.x);
    const deltaY = Math.abs(e.clientY - startPos.y);
    
    // 如果鼠标移动距离超过5px，判定为拖拽操作
    if (deltaX > 5 || deltaY > 5) {
      setIsDragging(true);
    }
  };

  /**
   * 处理点击事件
   * 只有在非拖拽状态下才触发卡片翻转
   */
  const handleClick = () => {
    // 只有在非拖拽状态下才触发翻转
    if (!isDragging) {
      setIsFlipped(!isFlipped);
    }
    // 重置状态，准备下次交互
    setIsDragging(false);
    setStartPos({ x: 0, y: 0 });
  };

  return (
    <Box
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      sx={{
        cursor: "pointer",
        aspectRatio: "1 / 1.14", // 卡片宽高比
      }}>
      {/* 3D翻转容器 */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d", // 保持子元素3D效果
          transition: "transform 0.6s", // 翻转动画时长
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)", // 根据状态决定翻转角度
        }}>
        {/* === 卡片正面 === */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backfaceVisibility: "hidden", // 背面不可见
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            // 根据主题模式设置背景渐变
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(180deg, #232629 0%, #161A1E 100%)"
                : "linear-gradient(180deg, #F2F2F2 0%, #FFFAF5 100%)",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "1rem",
            px: {xs: 3, md: 4},
            py: 5,
            transition: "border-color 0.2s ease",
            // Hover效果：边框颜色变化和宽度增加
            "&:hover": {
              borderColor: theme.palette.primary.light,
            },
          }}>
          {/* 顶部内容区域：标题和箭头图标 */}
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1.75rem", mb: 2 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "var(--font-size-xl)",
              }}>
              {t(`modules.${titleKey}.title` as any)}
            </Typography>
            <Box flexShrink={0}>
              <CircleArrowRight />
            </Box>
          </Box>
          {/* 底部图片区域 - 自适应缩放 */}
          <Box 
            sx={{ 
              display: "flex",
              alignItems: "center", // 垂直居中
              justifyContent: "center", // 水平居中
              overflow: "hidden", // 防止图片溢出
              minHeight: 0, // 允许flex项目缩小到内容以下
              px: "1.25rem"
            }}>
            <img 
              src={imageUrl} 
              style={{ 
                maxWidth: "100%", 
                maxHeight: "100%", 
                width: "auto", 
                height: "auto",
                objectFit: "contain" // 保持图片比例，完整显示
              }} 
            />
          </Box>
        </Box>

        {/* === 卡片背面 === */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backfaceVisibility: "hidden", // 背面不可见
            transform: "rotateY(180deg)", // 背面需要预先翻转180度
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            // 背面使用相反的渐变方向
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(180deg, var(--Color-Gary-900, #161A1E) 0%, var(--Color-Gary-850, #232629) 100%)"
                : "linear-gradient(180deg, var(--Color-Gold-100, #FFFAF5) 0%, var(--Color-Gary-100, #F2F2F2) 100%)",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "1rem",
            px: 3,
            py: 5,
            transition: "border-color 0.2s ease",
            "&:hover": {
              borderColor: theme.palette.primary.light,
            },
          }}>
          {/* 背面标题 */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "var(--font-size-xl)",
              mb: "1.75rem",
              color: "text.tertiary", // 较淡的颜色作为副标题
            }}>
            {t(`modules.${titleKey}.title` as any)}
          </Typography>
          {/* 背面详细描述 */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "var(--font-size-xl)",
              color: "text.primary",
              lineHeight: 1.5,
            }}>
            {t(`modules.${titleKey}.description` as any)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function CircleArrowRight() {
  const theme = useTheme();
  const strokeColor = theme.palette.mode === "dark" ? "#F2F2F2" : "#2D3135";
  
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect x="0.5" y="0.5" width="31" height="31" rx="15.5" stroke={strokeColor} />
      <path
        d="M16 24L23.9139 16.0861L15.8279 7.99999"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M23 16L8 16" stroke={strokeColor} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
